import React, { useState, useEffect } from 'react';
import { IoCheckmarkCircle, IoMegaphone, IoStar, IoAlertCircle } from 'react-icons/io5';
import { getCheckedAndEvaluated } from '../../api/MemberMeetingApi';
import EvaluationModal from '../layouts/EvaluationModal';
import CheckModal from '../layouts/CheckModal';
import useAuthStore from '../../store/authStore';

const SystemChatContainer = ({ content, createdAt, meetingId, membersMap, placeLatitude, placeLongitude }) => {
    const { member } = useAuthStore();
    const [checked, setChecked] = useState(false);
    const [evaluated, setEvaluated] = useState(false);
    const [showEvaluationModal, setShowEvaluationModal] = useState(false);
    const [showCheckModal, setShowCheckModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [remainingTime, setRemainingTime] = useState(0);

    // CHECK 또는 EVALUATION 케이스에서 상태 확인
    useEffect(() => {
        if ((content === 'CHECK' || content === 'EVALUATION') && meetingId) {
            checkStatus();
        }
    }, [content, meetingId]);

    // CHECK 타이머 관리
    useEffect(() => {
        if (content === 'CHECK' && createdAt && !checked) {
            const calculateRemainingTime = () => {
                const created = new Date(createdAt);
                const now = new Date();
                const timeLimit = 30 * 60 * 1000; // 30분 (밀리초)
                const elapsed = now - created;
                const remaining = Math.max(0, timeLimit - elapsed);

                return Math.floor(remaining / 1000); // 초 단위로 반환
            };

            // 초기 시간 설정
            setRemainingTime(calculateRemainingTime());

            // 1초마다 업데이트
            const timer = setInterval(() => {
                const remaining = calculateRemainingTime();
                setRemainingTime(remaining);

                if (remaining <= 0) {
                    clearInterval(timer);
                }
            }, 1000);

            return () => clearInterval(timer);
        }
    }, [content, createdAt, checked]);

    const checkStatus = async () => {
        try {
            setLoading(true);
            const response = await getCheckedAndEvaluated(meetingId);
            setChecked(response.checked);
            setEvaluated(response.evaluated);
        } catch (error) {
            console.error('상태 확인 실패:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCheckClick = () => {
        if (!checked) {
            setShowCheckModal(true);
        }
    };

    const handleEvaluationClick = () => {
        if (!evaluated) {
            setShowEvaluationModal(true);
        }
    };

    const handleEvaluationComplete = () => {
        setShowEvaluationModal(false);
        setEvaluated(true);
    };

    const handleEvaluationCancel = () => {
        setShowEvaluationModal(false);
    };

    const handleCheckComplete = () => {
        setShowCheckModal(false);
        setChecked(true);
    };

    const handleCheckCancel = () => {
        setShowCheckModal(false);
    };

    // membersMap을 배열로 변환
    const membersArray = Array.from(membersMap.values());

    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    const isCheckTimeExpired = remainingTime <= 0;

    const renderSystemMessage = () => {
        switch (content) {
            case 'CHECK':
                return (
                    <div className={`bg-white border border-gray-300 rounded-lg px-4 py-4 flex flex-col items-center ${
                        checked ? 'bg-gray-50' : ''
                    }`}>
                        {/* 상단 정보 영역 */}
                        <div className="flex items-center mb-3">
                            {/* 좌측 아이콘 */}
                            <div className="flex-shrink-0 mr-4">
                                <IoAlertCircle className={`${checked ? 'text-gray-400' : 'text-amber-500'}`} size={28} />
                            </div>

                            {/* 텍스트 영역 */}
                            <div className="flex-1">
                                <span className={`text-sm leading-relaxed ${checked ? 'text-gray-500' : 'text-gray-700'}`}>
                                    {checked
                                        ? '출석체크가 완료되었습니다.'
                                        : '출석 체크 시간입니다. 현재 위치 기반으로 모임에 출석해 주세요. 출석체크를 하면 쌀 500알이 환급됩니다.'
                                    }
                                </span>
                            </div>
                        </div>

                        {/* 타이머 및 출석하기 버튼 */}
                        {!checked && (
                            <div className="flex flex-col items-center space-y-3 w-full">
                                {/* 타이머 */}
                                <div className={`text-lg font-bold ${
                                    isCheckTimeExpired ? 'text-red-500' : 'text-black'
                                }`}>
                                    {isCheckTimeExpired ? '00:00' : formatTime(remainingTime)}
                                </div>

                                {/* 출석하기 버튼 */}
                                <button
                                    onClick={handleCheckClick}
                                    disabled={isCheckTimeExpired}
                                    className={`w-1/2 py-3 font-medium rounded-lg transition-colors ${
                                        isCheckTimeExpired 
                                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                            : 'bg-amber-500 active:bg-amber-600 text-white'
                                    }`}
                                >
                                    {isCheckTimeExpired ? '출석 시간 만료' : '출석하기'}
                                </button>
                            </div>
                        )}
                    </div>
                );

            case 'CONFIRMED':
                return (
                    <div className="flex items-center justify-center space-x-2 bg-blue-50 px-4 py-3 rounded-lg">
                        <IoCheckmarkCircle className="text-blue-500" size={20} />
                        <span className="text-gray-700 text-sm">모임이 확정되었습니다</span>
                    </div>
                );

            case 'EVALUATION':
                return (
                    <div className={`bg-white border border-gray-300 rounded-lg px-4 py-4 flex flex-col items-center ${
                        evaluated ? 'bg-gray-50' : ''
                    }`}>
                        {/* 상단 정보 영역 */}
                        <div className="flex items-center mb-3">
                            {/* 좌측 아이콘 */}
                            <div className="flex-shrink-0 mr-4">
                                <IoStar className={`${evaluated ? 'text-gray-400' : 'text-blue-500'}`} size={28} />
                            </div>

                            {/* 텍스트 영역 */}
                            <div className="flex-1">
                                <span className={`text-sm leading-relaxed ${evaluated ? 'text-gray-500' : 'text-gray-700'}`}>
                                    {evaluated
                                        ? '밥친구들의 매너 평가를 완료하였습니다.'
                                        : '모임 시간입니다! 모임이 끝나셨다면 함께 한 밥친구들의 매너를 평가해주세요.'
                                    }
                                </span>
                            </div>
                        </div>

                        {/* 매너평가하기 버튼 */}
                        {!evaluated && (
                            <button
                                onClick={handleEvaluationClick}
                                className="w-1/2 py-3 bg-blue-500 active:bg-blue-600 text-white font-medium rounded-lg transition-colors"
                            >
                                매너평가하기
                            </button>
                        )}
                    </div>
                );

            case 'ANNOUNCEMENT':
                return (
                    <div className="flex items-center justify-center space-x-2 bg-purple-50 px-4 py-3 rounded-lg">
                        <IoMegaphone className="text-purple-500" size={20} />
                        <span className="text-gray-700">관리자의 공지사항이 있습니다</span>
                    </div>
                );

            case "END_MINCAPACITY":
                return (
                    <div className="flex items-center justify-center space-x-2 bg-gray-50 px-4 py-3 rounded-lg">
                        <IoAlertCircle className="text-gray-500" size={20} />
                        <span className="text-gray-700 text-sm">모임의 최소 인원이 충족되지 않아 모임이 종료되었습니다.</span>
                    </div>
                );

            default:
                return (
                    <div className="flex items-center justify-center bg-gray-50 px-4 py-3 rounded-lg">
                        <span className="text-gray-500">{content}</span>
                    </div>
                );
        }
    };

    return (
        <>
            <div className="flex flex-col items-center my-4">
                {loading ? (
                    <div className="bg-white border border-gray-300 rounded-lg flex items-center px-4 py-4">
                        <span className="text-gray-500">로딩 중...</span>
                    </div>
                ) : (
                    renderSystemMessage()
                )}
                {createdAt && (
                    <span className="text-xs text-gray-400 mt-1">
                        {new Date(createdAt).toLocaleTimeString('ko-KR', {
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                        })}
                    </span>
                )}
            </div>

            {/* 평가 모달 */}
            <EvaluationModal
                isVisible={showEvaluationModal}
                onClose={handleEvaluationCancel}
                onComplete={handleEvaluationComplete}
                members={membersArray}
                meetingId={meetingId}
                currentUserId={member?.id}
            />

            {/* 출석 체크 모달 */}
            <CheckModal
                isVisible={showCheckModal}
                onClose={handleCheckCancel}
                onComplete={handleCheckComplete}
                meetingId={meetingId}
                placeLatitude={placeLatitude}
                placeLongitude={placeLongitude}
            />
        </>
    );
};

export default SystemChatContainer;
